import { makeAutoObservable, runInAction } from "mobx";
import type { RootStore, ServiceStore } from "@intrasync/stores";
import type { evereeWorkerData } from "types/api-types/payroll/everee-worker-data/everee-worker-data.types";
import type { user as User } from "types/api-types/users/users.types";
import type {
  csvRow as CsvRow,
  csvEmployee as CsvEmployee,
  preprocessedShift as PreprocessedShift,
  timesheetCsvImportResult as TimesheetCsvImportResult,
} from "types/api-types/payroll/timesheet-csv-import/timesheet-csv-import.types";

export const STEP_LABELS = [
  "Upload CSV",
  "Map Employees",
  "Review & Edit",
  "Leave Approval",
  "Results",
] as const;

export interface AuditEntry {
  rowIndex: number;
  field: string;
  oldValue: string;
  newValue: string;
  timestamp: number;
  userId: string;
}

const REQUIRED_HEADERS = new Set([
  "NUMBER",
  "FIRSTNAME",
  "LASTNAME",
  "DATEIN",
  "TIMEIN",
  "DATEOUT",
  "TIMEOUT",
  "JOBCODE",
  "LEAVECODE",
  "REGHOURS",
  "O2HOURS",
  "O1HOURS",
]);

// PC files use digit zero (02HOURS, 01HOURS) instead of letter O
const HEADER_ALIASES: Record<string, string> = {
  "02HOURS": "O2HOURS",
  "01HOURS": "O1HOURS",
};

export type MappingSource = "saved" | "auto" | "manual";

/** Leave code "0" means no leave in TCP exports; anything else is a real leave code. */
export const isLeaveCode = (code: string): boolean => {
  const trimmed = code.trim();
  return trimmed !== "" && trimmed !== "0";
};

/** Convert decimal hours (e.g. 7.43) to HH:MM format (e.g. "7:26") for reconciliation. */
export const decimalToHHMM = (decimal: number): string => {
  if (isNaN(decimal) || decimal === 0) return "0:00";
  const hours = Math.floor(Math.abs(decimal));
  const minutes = Math.round((Math.abs(decimal) - hours) * 60);
  const sign = decimal < 0 ? "-" : "";
  return `${sign}${hours}:${minutes.toString().padStart(2, "0")}`;
};

/** Job code name mappings per company. Numeric TCP codes → friendly names.
 *  The LEAVECODE column is just a 0/1 flag — the actual leave type comes
 *  from the JOBCODE column (e.g. 1000 = Vacation). */
const JOB_CODE_NAMES: Record<string, Record<string, string>> = {
  default: {
    "1000": "Vacation",
    "1002": "PTO",
    "1004": "Hourly",
    "1006": "Sick",
  },
  CPP: {
    "1000": "Vacation",
    "1002": "PTO",
    "1004": "Hourly",
    "1006": "Sick",
  },
};

/** Detect if an hours value uses HH:MM format (e.g. "7:26") vs decimal (e.g. "7.43"). */
const isHHMMFormat = (value: string): boolean => /^\d+:\d{2}$/.test(value.trim());

/** Convert an HH:MM string (e.g. "7:26") to decimal hours (e.g. 7.43). */
const hhmmToDecimal = (value: string): string => {
  const trimmed = value.trim();
  if (!isHHMMFormat(trimmed)) return trimmed;
  const [h, m] = trimmed.split(":").map(Number);
  return (h + m / 60).toFixed(2);
};

/**
 * TCP reports decimal hours (base-100) converted from base-60 time.
 * Individual shift values are rounded, so summing 15-20 rows can accumulate
 * ±0.02 hrs of rounding artifact. If a total is within that range of any
 * whole number, snap to it. We round the diff to 2 decimal places first
 * to avoid floating-point noise from parseFloat summation.
 * e.g. 79.99 → 80, 80.01 → 80, 80.02 → 80, 71.99 → 72
 */
const SNAP_THRESHOLD = 0.02;
const snapToWholeHours = (total: number): number | null => {
  const rounded = Math.round(total);
  const diff = Math.round(Math.abs(total - rounded) * 100) / 100;
  if (diff > 0 && diff <= SNAP_THRESHOLD) {
    return rounded;
  }
  return null;
};

class TcpCsvImportStore {
  rootStore: RootStore;

  // Wizard state
  currentStep = 0;
  isLoading = false;
  error: string | null = null;

  // Step 1 - File Upload
  fileName = "";
  csvContent = "";
  csvRows: CsvRow[] = [];
  csvEmployees: CsvEmployee[] = [];
  headerValid = false;
  previewSearch = "";
  fileIsHHMMFormat = false;

  // Step 2 - Employee Mapping
  employeeMapping: Map<string, string> = new Map(); // tcpNumber -> evereeWorkerId
  mappingSources: Map<string, MappingSource> = new Map(); // tcpNumber -> source
  workerData: evereeWorkerData[] = [];
  companyUsers: User[] = []; // users with tcpWorkerId/evereeWorkerId
  timezone = "America/Los_Angeles";
  isSavingMappings = false;

  // Global search (Steps 2-4)
  globalSearch = "";

  // Expand/Collapse (Step 3 per-employee breakdown)
  expandedEmployees: Set<string> = new Set();

  // Step 3 - Review & Edit
  rowEdits: Map<number, Partial<CsvRow>> = new Map(); // rowIndex -> changed fields
  auditLog: AuditEntry[] = [];
  duplicateWarning: string | null = null;
  duplicateOverridden = false;

  // Step 4 - Leave Approval
  leaveApprovals: Map<number, boolean> = new Map(); // rowIndex -> approved

  // Step 5 - Results
  isSubmitting = false;
  importResult: TimesheetCsvImportResult | null = null;
  activeResultTab = 0;
  succeededRowIndices: Set<number> = new Set(); // CSV row indices that already landed in Everee

  constructor(rootStore: RootStore) {
    this.rootStore = rootStore;
    makeAutoObservable(this, { rootStore: false });
  }

  // ServiceStore accessors
  get evereeWorkerDataStore(): ServiceStore {
    return this.rootStore.evereeWorkerDataStore as ServiceStore;
  }

  get userStore(): ServiceStore {
    return this.rootStore.userStore as ServiceStore;
  }

  // Global search action
  setGlobalSearch = (value: string) => {
    this.globalSearch = value;
  };

  // Expand/collapse actions
  toggleEmployeeExpanded = (empNumber: string) => {
    if (this.expandedEmployees.has(empNumber)) {
      this.expandedEmployees.delete(empNumber);
    } else {
      this.expandedEmployees.add(empNumber);
    }
  };

  isEmployeeExpanded = (empNumber: string): boolean => {
    return this.expandedEmployees.has(empNumber);
  };

  /** Get individual CSV rows for a specific employee (for expand/collapse detail). */
  getEmployeeRows = (empNumber: string): { row: CsvRow; index: number }[] => {
    return this.csvRows
      .map((row, index) => ({ row, index }))
      .filter(({ row }) => row.number === empNumber);
  };

  /** Get a job code's friendly name based on selected company. */
  getJobCodeName = (code: string): string => {
    const companyId =
      (this.rootStore.routerStore?.queryParams?.companyId as string) || "";
    const companyMap = JOB_CODE_NAMES[companyId] || JOB_CODE_NAMES.default;
    return companyMap[code] || code;
  };

  /** Employees filtered by global search (for steps 2-4). */
  get filteredCsvEmployees(): CsvEmployee[] {
    const search = this.globalSearch.trim().toLowerCase();
    if (!search) return this.csvEmployees;
    return this.csvEmployees.filter(
      (emp) =>
        emp.number.toLowerCase().includes(search) ||
        emp.firstName.toLowerCase().includes(search) ||
        emp.lastName.toLowerCase().includes(search) ||
        `${emp.firstName} ${emp.lastName}`.toLowerCase().includes(search)
    );
  }

  /** CSV rows filtered by global search (for steps 2-4). */
  get filteredCsvRows(): CsvRow[] {
    const search = this.globalSearch.trim().toLowerCase();
    if (!search) return this.csvRows;
    return this.csvRows.filter(
      (r) =>
        r.number.toLowerCase().includes(search) ||
        r.firstName.toLowerCase().includes(search) ||
        r.lastName.toLowerCase().includes(search) ||
        `${r.firstName} ${r.lastName}`.toLowerCase().includes(search)
    );
  }

  /** Mapping source for a given TCP number. */
  getMappingSource = (tcpNumber: string): MappingSource | null => {
    return this.mappingSources.get(tcpNumber) || null;
  };

  /** Whether there are any unsaved mapping changes (auto or manual mappings not yet persisted). */
  get hasUnsavedMappings(): boolean {
    for (const [, source] of this.mappingSources) {
      if (source === "auto" || source === "manual") return true;
    }
    return false;
  }

  // Computed values
  get totalRows(): number {
    return this.csvRows.length;
  }

  get leaveRowCount(): number {
    return this.csvRows.filter((r) => isLeaveCode(r.leaveCode)).length;
  }

  get workRowCount(): number {
    return this.totalRows - this.leaveRowCount;
  }

  get leaveRows(): { row: CsvRow; index: number }[] {
    return this.csvRows
      .map((row, index) => ({ row, index }))
      .filter(({ row }) => {
        if (!isLeaveCode(row.leaveCode)) return false;
        // Filter out zero-hour leave entries (Phase 1F)
        const reg = parseFloat(row.regHours) || 0;
        const ot = parseFloat(row.o2Hours) || 0;
        return reg !== 0 || ot !== 0;
      });
  }

  get leaveRowsByEmployee(): Map<string, { row: CsvRow; index: number }[]> {
    const grouped = new Map<string, { row: CsvRow; index: number }[]>();
    this.leaveRows.forEach(({ row, index }) => {
      const key = row.number;
      const existing = grouped.get(key) || [];
      existing.push({ row, index });
      grouped.set(key, existing);
    });
    return grouped;
  }

  get approvedLeaveCount(): number {
    let count = 0;
    this.leaveApprovals.forEach((approved) => {
      if (approved) count++;
    });
    return count;
  }

  get rejectedLeaveCount(): number {
    return this.leaveRowCount - this.approvedLeaveCount;
  }

  /** Leave summary grouped by job code (the actual leave type). */
  get leaveSummaryByCodes(): Map<string, number> {
    const counts = new Map<string, number>();
    this.leaveRows.forEach(({ row, index }) => {
      const approved = this.leaveApprovals.get(index);
      if (approved) {
        const effective = this.getEffectiveRow(index);
        const jobCode = effective.jobCode || row.jobCode;
        counts.set(jobCode, (counts.get(jobCode) || 0) + 1);
      }
    });
    return counts;
  }

  get uniqueLeaveCodes(): string[] {
    const codes = new Set<string>();
    this.csvRows.forEach((r) => {
      if (isLeaveCode(r.leaveCode)) codes.add(r.leaveCode.trim());
    });
    return Array.from(codes).sort();
  }

  get dateRange(): { start: string; end: string } | null {
    if (this.csvRows.length === 0) return null;

    const dates = this.csvRows
      .map((r) => r.dateIn)
      .filter((d) => d.trim() !== "")
      .sort();

    if (dates.length === 0) return null;
    return { start: dates[0], end: dates[dates.length - 1] };
  }

  get previewRows(): CsvRow[] {
    const search = this.previewSearch.trim().toLowerCase();
    if (!search) return this.csvRows.slice(0, 20);

    return this.csvRows.filter(
      (r) =>
        r.number.toLowerCase().includes(search) ||
        r.firstName.toLowerCase().includes(search) ||
        r.lastName.toLowerCase().includes(search) ||
        `${r.firstName} ${r.lastName}`.toLowerCase().includes(search)
    );
  }

  get isPreviewFiltered(): boolean {
    return this.previewSearch.trim() !== "";
  }

  setPreviewSearch = (value: string) => {
    this.previewSearch = value;
  };

  get unmappedCount(): number {
    let count = 0;
    this.csvEmployees.forEach((emp) => {
      if (!this.employeeMapping.has(emp.number)) count++;
    });
    return count;
  }

  get mappedCount(): number {
    return this.csvEmployees.length - this.unmappedCount;
  }

  get canProceedFromStep1(): boolean {
    return this.headerValid && this.csvRows.length > 0;
  }

  get canProceedFromStep2(): boolean {
    return this.unmappedCount === 0 && this.csvEmployees.length > 0;
  }

  getEffectiveRow = (index: number): CsvRow => {
    const original = this.csvRows[index];
    const edits = this.rowEdits.get(index);
    if (!edits) return original;
    return { ...original, ...edits };
  };

  get submissionSummary() {
    // Work rows (non-leave) always included; leave rows only if approved
    const approvedLeaveIndices = new Set<number>();
    this.leaveApprovals.forEach((approved, index) => {
      if (approved) approvedLeaveIndices.add(index);
    });

    const rowsToImport: CsvRow[] = [];
    this.csvRows.forEach((row, index) => {
      const isLeave = isLeaveCode(row.leaveCode);
      if (!isLeave || approvedLeaveIndices.has(index)) {
        rowsToImport.push(this.getEffectiveRow(index));
      }
    });

    const mappedRows = rowsToImport.filter((r) =>
      this.employeeMapping.has(r.number)
    );

    const uniqueWorkers = new Set(
      mappedRows.map((r) => this.employeeMapping.get(r.number))
    );

    const totalRegHours = mappedRows.reduce(
      (sum, r) => sum + (parseFloat(r.regHours) || 0),
      0
    );
    const totalO2Hours = mappedRows.reduce(
      (sum, r) => sum + (parseFloat(r.o2Hours) || 0),
      0
    );

    const leaveHours = mappedRows
      .filter((r) => isLeaveCode(r.leaveCode))
      .reduce((sum, r) => sum + (parseFloat(r.regHours) || 0), 0);

    return {
      totalRows: mappedRows.length,
      uniqueWorkers: uniqueWorkers.size,
      totalRegHours,
      totalO2Hours,
      totalHours: totalRegHours + totalO2Hours,
      leaveHours,
      approvedLeaveRows: this.approvedLeaveCount,
      rejectedLeaveRows: this.rejectedLeaveCount,
    };
  }

  get employeeBreakdown() {
    const breakdown: {
      name: string;
      regHours: number;
      o2Hours: number;
      totalHours: number;
      leaveHours: number;
    }[] = [];

    const approvedLeaveIndices = new Set<number>();
    this.leaveApprovals.forEach((approved, index) => {
      if (approved) approvedLeaveIndices.add(index);
    });

    this.csvEmployees.forEach((emp) => {
      let regHours = 0;
      let o2Hours = 0;
      let leaveHours = 0;

      this.csvRows.forEach((row, index) => {
        if (row.number !== emp.number) return;
        const isLeave = isLeaveCode(row.leaveCode);
        if (isLeave && !approvedLeaveIndices.has(index)) return;

        const effective = this.getEffectiveRow(index);
        const reg = parseFloat(effective.regHours) || 0;
        const ot2 = parseFloat(effective.o2Hours) || 0;

        regHours += reg;
        o2Hours += ot2;
        if (isLeave) leaveHours += reg;
      });

      breakdown.push({
        name: `${emp.firstName} ${emp.lastName}`,
        regHours,
        o2Hours,
        totalHours: regHours + o2Hours,
        leaveHours,
      });
    });

    return breakdown;
  }

  get succeededResults(): TimesheetCsvImportResult["results"] {
    return this.importResult?.results.filter((r) => r.success) || [];
  }

  get failedResults(): TimesheetCsvImportResult["results"] {
    return (
      this.importResult?.results.filter((r) => !r.success && !r.skipped) || []
    );
  }

  get skippedResults(): TimesheetCsvImportResult["results"] {
    return this.importResult?.results.filter((r) => r.skipped) || [];
  }

  get workerMap(): Map<string, string> {
    const map = new Map<string, string>();
    this.workerData.forEach((w) => {
      map.set(w.workerId, `${w.firstName} ${w.lastName}`);
    });
    return map;
  }

  // Step navigation
  setStep = (step: number) => {
    if (step >= 0 && step < STEP_LABELS.length) {
      this.currentStep = step;
    }
  };

  nextStep = () => {
    this.setStep(this.currentStep + 1);
  };

  prevStep = () => {
    this.setStep(this.currentStep - 1);
  };

  // Step 1 actions
  handleFileSelect = (file: File) => {
    this.fileName = file.name;
    this.error = null;

    const reader = new FileReader();
    reader.onload = (e) => {
      const text = e.target?.result as string;
      if (text) {
        runInAction(() => {
          this.csvContent = text;
          this.parseCSV(text);
        });
      }
    };
    reader.onerror = () => {
      runInAction(() => {
        this.error = "Failed to read file";
      });
    };
    reader.readAsText(file);
  };

  parseCSV = (text: string) => {
    this.csvRows = [];
    this.csvEmployees = [];
    this.headerValid = false;
    this.fileIsHHMMFormat = false;
    this.error = null;

    const lines = text.split("\n").filter((line) => line.trim() !== "");
    if (lines.length < 2) {
      this.error = "CSV file must have a header row and at least one data row";
      return;
    }

    // Parse, normalize, and validate headers
    const headerLine = lines[0];
    const rawHeaders = headerLine
      .split(",")
      .map((h) => h.trim().replace(/^["<>]+|["<>]+$/g, "").toUpperCase());

    // Normalize aliases (e.g. PC files: 02HOURS → O2HOURS)
    const headers = rawHeaders.map((h) => HEADER_ALIASES[h] || h);

    // Validate all required columns are present (order-independent)
    const headerSet = new Set(headers);
    const missing = [...REQUIRED_HEADERS].filter((h) => !headerSet.has(h));

    if (missing.length > 0) {
      this.error = `Missing CSV columns: ${missing.join(", ")}`;
      return;
    }

    this.headerValid = true;

    // Build column index map so column order doesn't matter
    const col = new Map<string, number>();
    headers.forEach((h, i) => {
      if (REQUIRED_HEADERS.has(h)) col.set(h, i);
    });

    // Auto-detect HH:MM vs decimal format from the first data row
    const firstDataFields = lines[1].split(",");
    const firstRegHours = firstDataFields[col.get("REGHOURS")!]
      ?.trim()
      .replace(/^"+|"+$/g, "");
    const detectedHHMM = isHHMMFormat(firstRegHours || "");
    this.fileIsHHMMFormat = detectedHHMM;

    // Parse data rows
    const rows: CsvRow[] = [];
    const employeeMap = new Map<string, CsvEmployee>();

    // Strip surrounding quotes from CSV field values
    const stripQuotes = (val: string) => val.trim().replace(/^"+|"+$/g, "");

    const field = (fields: string[], header: string) =>
      stripQuotes(fields[col.get(header)!]);

    for (let i = 1; i < lines.length; i++) {
      const fields = lines[i].split(",");
      if (fields.length < col.size) continue;

      let regHours = field(fields, "REGHOURS");
      let o1Hours = field(fields, "O1HOURS");
      let o2Hours = field(fields, "O2HOURS");

      // Convert HH:MM values to decimal and normalize overtime columns.
      // HH:MM files (e.g. RVP) use O1HOURS for overtime; decimal files use O2HOURS.
      if (detectedHHMM) {
        regHours = hhmmToDecimal(regHours);
        o1Hours = hhmmToDecimal(o1Hours);
        o2Hours = hhmmToDecimal(o2Hours);
        // Normalize: move O1 overtime into O2 so all downstream code reads o2Hours
        o2Hours = o1Hours;
        o1Hours = "0";
      }

      const row: CsvRow = {
        number: field(fields, "NUMBER"),
        firstName: field(fields, "FIRSTNAME"),
        lastName: field(fields, "LASTNAME"),
        dateIn: field(fields, "DATEIN"),
        timeIn: field(fields, "TIMEIN"),
        dateOut: field(fields, "DATEOUT"),
        timeOut: field(fields, "TIMEOUT"),
        jobCode: field(fields, "JOBCODE"),
        leaveCode: field(fields, "LEAVECODE"),
        regHours,
        o2Hours,
        o1Hours,
      };

      rows.push(row);

      // Build employee summary
      const existing = employeeMap.get(row.number);
      const isLeave = isLeaveCode(row.leaveCode);

      if (existing) {
        existing.rowCount++;
        if (isLeave) existing.leaveRowCount++;
        existing.totalRegHours += parseFloat(row.regHours) || 0;
        existing.totalO1Hours += parseFloat(row.o1Hours) || 0;
        existing.totalO2Hours += parseFloat(row.o2Hours) || 0;
      } else {
        employeeMap.set(row.number, {
          number: row.number,
          firstName: row.firstName,
          lastName: row.lastName,
          rowCount: 1,
          leaveRowCount: isLeave ? 1 : 0,
          totalRegHours: parseFloat(row.regHours) || 0,
          totalO1Hours: parseFloat(row.o1Hours) || 0,
          totalO2Hours: parseFloat(row.o2Hours) || 0,
        });
      }
    }

    // Snap employee reg-hour totals to the nearest whole number when within
    // ±0.01 hrs (base-60 → base-100 rounding artifact, < 1 minute).
    // Only applies to decimal-format files; HH:MM files are already precise.
    if (!detectedHHMM) {
      for (const [empNumber, emp] of employeeMap) {
        const allIndices: number[] = [];
        const nonLeaveIndices: number[] = [];
        for (let idx = 0; idx < rows.length; idx++) {
          if (rows[idx].number !== empNumber) continue;
          allIndices.push(idx);
          if (!isLeaveCode(rows[idx].leaveCode)) nonLeaveIndices.push(idx);
        }

        if (nonLeaveIndices.length === 0) continue;

        const regSum = allIndices.reduce(
          (sum, idx) => sum + (parseFloat(rows[idx].regHours) || 0),
          0
        );

        const snapTarget = snapToWholeHours(regSum);
        if (snapTarget !== null) {
          const lastIdx = nonLeaveIndices[nonLeaveIndices.length - 1];
          const lastRow = rows[lastIdx];
          const currentVal = parseFloat(lastRow.regHours) || 0;
          const adjusted = currentVal + (snapTarget - regSum);
          lastRow.regHours = adjusted.toFixed(2);
          emp.totalRegHours = snapTarget;
        }
      }
    }

    this.csvRows = rows;
    this.csvEmployees = Array.from(employeeMap.values());
  };

  // Step 2 actions
  loadWorkerData = async (companyId?: string) => {
    try {
      const query: Record<string, unknown> = { page: 0, size: 100 };
      if (companyId) query.companyId = companyId;

      await this.evereeWorkerDataStore.find({ query });

      runInAction(() => {
        const raw = (this.evereeWorkerDataStore?.entities ||
          []) as unknown as Record<string, unknown>[];
        // API returns 'id' but our type uses 'workerId' - normalize the field
        this.workerData = raw.map((e) => ({
          ...e,
          workerId: (e.workerId || e.id) as string,
        })) as unknown as evereeWorkerData[];
      });
    } catch (err) {
      runInAction(() => {
        this.error =
          err instanceof Error ? err.message : "Failed to load worker data";
      });
    }

    // Also load users with saved TCP/Everee mappings for this company
    try {
      const userQuery: Record<string, unknown> = {
        $limit: 500,
        tcpWorkerId: { $ne: "" },
        evereeWorkerId: { $ne: "" },
      };
      if (companyId) userQuery.companyId = companyId;

      await this.userStore.find({ query: userQuery });

      runInAction(() => {
        this.companyUsers = (this.userStore?.entities || []) as unknown as User[];
      });
    } catch {
      // Non-blocking — user mapping lookup is optional
    }
  };

  autoMapEmployees = () => {
    this.employeeMapping.clear();
    this.mappingSources.clear();

    const workers = this.workerData;

    // First pass: apply saved mappings from user records (tcpWorkerId → evereeWorkerId)
    this.csvEmployees.forEach((emp) => {
      const savedUser = this.companyUsers.find(
        (u) => u.tcpWorkerId === emp.number && u.evereeWorkerId
      );
      if (savedUser?.evereeWorkerId) {
        this.employeeMapping.set(emp.number, savedUser.evereeWorkerId);
        this.mappingSources.set(emp.number, "saved");
      }
    });

    // Second pass: fuzzy name-match for any remaining unmatched employees
    this.csvEmployees.forEach((emp) => {
      if (this.employeeMapping.has(emp.number)) return;

      const csvFirstName = emp.firstName.toLowerCase().trim();
      const csvLastName = emp.lastName.toLowerCase().trim();

      for (const worker of workers) {
        const wFirstName = worker.firstName?.toLowerCase().trim() || "";
        const wLastName = worker.lastName?.toLowerCase().trim() || "";

        if (
          csvFirstName &&
          csvLastName &&
          csvFirstName === wFirstName &&
          csvLastName === wLastName
        ) {
          this.employeeMapping.set(emp.number, worker.workerId);
          this.mappingSources.set(emp.number, "auto");
          return;
        }
      }
    });
  };

  setEmployeeMapping = (tcpNumber: string, evereeWorkerId: string) => {
    if (evereeWorkerId) {
      this.employeeMapping.set(tcpNumber, evereeWorkerId);
      // Only mark as manual if it wasn't already a saved mapping
      if (this.mappingSources.get(tcpNumber) !== "saved") {
        this.mappingSources.set(tcpNumber, "manual");
      }
    } else {
      this.employeeMapping.delete(tcpNumber);
      this.mappingSources.delete(tcpNumber);
    }
  };

  /** Persist all current mappings to user records (patch tcpWorkerId + evereeWorkerId). */
  saveMappings = async () => {
    this.isSavingMappings = true;
    try {
      const companyId = this.rootStore.routerStore?.queryParams
        ?.companyId as string;

      const patches: Promise<unknown>[] = [];
      this.employeeMapping.forEach((evereeWorkerId, tcpNumber) => {
        const source = this.mappingSources.get(tcpNumber);
        // Only save mappings that aren't already persisted
        if (source === "saved") return;

        const emp = this.csvEmployees.find((e) => e.number === tcpNumber);
        // Find an existing user to patch, or find by name
        const existingUser = this.companyUsers.find(
          (u) => u.tcpWorkerId === tcpNumber
        ) || (this.userStore?.entities as unknown as User[])?.find(
          (u) =>
            u.companyId === companyId &&
            u.firstName?.toLowerCase() === emp?.firstName?.toLowerCase() &&
            u.lastName?.toLowerCase() === emp?.lastName?.toLowerCase()
        );

        if (existingUser?.id) {
          patches.push(
            this.userStore.patch(existingUser.id, {
              tcpWorkerId: tcpNumber,
              evereeWorkerId,
            })
          );
        }
      });

      await Promise.all(patches);

      runInAction(() => {
        // Mark all saved
        this.employeeMapping.forEach((_workerId, tcpNumber) => {
          this.mappingSources.set(tcpNumber, "saved");
        });
      });
    } catch (err) {
      runInAction(() => {
        this.error =
          err instanceof Error ? err.message : "Failed to save mappings";
      });
    } finally {
      runInAction(() => {
        this.isSavingMappings = false;
      });
    }
  };

  setTimezone = (tz: string) => {
    this.timezone = tz;
  };

  setActiveResultTab = (tab: number) => {
    this.activeResultTab = tab;
  };

  // Step 3 - Review & Edit actions
  setRowEdit = (rowIndex: number, field: keyof CsvRow, value: string) => {
    const original = this.csvRows[rowIndex];
    if (!original) return;

    const oldValue = original[field];
    if (oldValue === value) {
      // Remove edit if reverting to original
      const existing = this.rowEdits.get(rowIndex);
      if (existing) {
        const updated = { ...existing };
        delete updated[field];
        if (Object.keys(updated).length === 0) {
          this.rowEdits.delete(rowIndex);
        } else {
          this.rowEdits.set(rowIndex, updated);
        }
      }
      return;
    }

    const existing = this.rowEdits.get(rowIndex) || {};
    this.rowEdits.set(rowIndex, { ...existing, [field]: value });

    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const userId = (this.rootStore.userStore as any)?.user?.email || "unknown";
    this.auditLog.push({
      rowIndex,
      field,
      oldValue,
      newValue: value,
      timestamp: Date.now(),
      userId,
    });
  };

  isRowEdited = (rowIndex: number): boolean => {
    return this.rowEdits.has(rowIndex);
  };

  checkDuplicate = async () => {
    this.duplicateWarning = null;
    this.duplicateOverridden = false;

    const dateRange = this.dateRange;
    if (!dateRange) return;

    try {
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      const app = (this.rootStore as any).app;
      if (!app) return;

      const companyId = this.rootStore.routerStore?.queryParams
        ?.companyId as string;

      const result = await app.service("timesheet-csv-import").find({
        query: {
          checkDuplicate: true,
          startDate: dateRange.start,
          endDate: dateRange.end,
          companyId,
        },
      });

      runInAction(() => {
        if (result?.isDuplicate) {
          this.duplicateWarning = `This date range (${dateRange.start} - ${dateRange.end}) was previously imported on ${result.importedAt || "a prior date"}. You may re-import if needed.`;
        }
      });
    } catch {
      // Duplicate check is non-blocking; silently ignore errors
    }
  };

  overrideDuplicate = () => {
    this.duplicateOverridden = true;
  };

  // Step 4 - Leave Approval actions
  setLeaveApproval = (rowIndex: number, approved: boolean) => {
    this.leaveApprovals.set(rowIndex, approved);
  };

  approveAllLeave = () => {
    this.leaveRows.forEach(({ index }) => {
      this.leaveApprovals.set(index, true);
    });
  };

  rejectAllLeave = () => {
    this.leaveRows.forEach(({ index }) => {
      this.leaveApprovals.set(index, false);
    });
  };

  initLeaveApprovals = () => {
    // Default all leave rows to approved
    this.leaveRows.forEach(({ index }) => {
      if (!this.leaveApprovals.has(index)) {
        this.leaveApprovals.set(index, true);
      }
    });
  };

  // Submit actions
  submitImport = async () => {
    this.isSubmitting = true;
    this.error = null;
    this.importResult = null;

    try {
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      const app = (this.rootStore as any).app;
      if (!app) throw new Error("API not available");

      const companyId = this.rootStore.routerStore?.queryParams
        ?.companyId as string;

      // Build rejected leave row indices
      const rejectedLeaveIndices = new Set<number>();
      this.leaveApprovals.forEach((approved, index) => {
        if (!approved) rejectedLeaveIndices.add(index);
      });

      // Build pre-processed shifts, skipping already-succeeded rows
      const shifts: PreprocessedShift[] = [];
      const shiftRowMap: number[] = []; // shifts[i] came from csvRows[shiftRowMap[i]]

      this.csvRows.forEach((row, index) => {
        if (this.succeededRowIndices.has(index)) return;

        const isLeave = isLeaveCode(row.leaveCode);
        if (isLeave && rejectedLeaveIndices.has(index)) return;

        const effective = this.getEffectiveRow(index);
        const workerId = this.employeeMapping.get(row.number);
        if (!workerId) return;

        shiftRowMap.push(index);
        shifts.push({
          workerId,
          employeeName: `${row.firstName} ${row.lastName}`,
          dateIn: effective.dateIn,
          timeIn: effective.timeIn,
          dateOut: effective.dateOut,
          timeOut: effective.timeOut,
          regHours: parseFloat(effective.regHours) || 0,
          overtimeHours: parseFloat(effective.o2Hours) || 0,
        });
      });

      if (shifts.length === 0) {
        runInAction(() => {
          this.error = "No shifts to import — all rows already succeeded.";
          this.isSubmitting = false;
        });
        return;
      }

      const result: TimesheetCsvImportResult = await app
        .service("timesheet-csv-import")
        .create(
          {
            shifts,
            timezone: this.timezone,
            sourceFileName: this.fileName || undefined,
            payPeriodStart: this.dateRange
              ? new Date(this.dateRange.start).toISOString()
              : undefined,
            payPeriodEnd: this.dateRange
              ? new Date(this.dateRange.end).toISOString()
              : undefined,
            allowReimport: this.duplicateOverridden || undefined,
          },
          { query: { companyId } }
        );

      runInAction(() => {
        // Mark succeeded shifts so they're excluded on retry
        result.results.forEach((r, i) => {
          if (r.success && shiftRowMap[i] !== undefined) {
            this.succeededRowIndices.add(shiftRowMap[i]);
          }
        });
        this.importResult = result;
        this.currentStep = 4;
      });
    } catch (err) {
      runInAction(() => {
        this.error =
          err instanceof Error ? err.message : "Failed to submit import";
      });
    } finally {
      runInAction(() => {
        this.isSubmitting = false;
      });
    }
  };

  get hasFailedShifts(): boolean {
    return (this.importResult?.failed ?? 0) > 0;
  }

  get retryShiftCount(): number {
    const total = this.csvRows.length;
    return total - this.succeededRowIndices.size;
  }

  // Reset
  reset = () => {
    this.currentStep = 0;
    this.isLoading = false;
    this.error = null;
    this.fileName = "";
    this.csvContent = "";
    this.csvRows = [];
    this.csvEmployees = [];
    this.headerValid = false;
    this.fileIsHHMMFormat = false;
    this.previewSearch = "";
    this.employeeMapping.clear();
    this.mappingSources.clear();
    this.workerData = [];
    this.companyUsers = [];
    this.timezone = "America/Los_Angeles";
    this.isSavingMappings = false;
    this.globalSearch = "";
    this.expandedEmployees.clear();
    this.rowEdits.clear();
    this.auditLog = [];
    this.duplicateWarning = null;
    this.duplicateOverridden = false;
    this.leaveApprovals.clear();
    this.isSubmitting = false;
    this.importResult = null;
    this.activeResultTab = 0;
    this.succeededRowIndices.clear();
  };
}

export default TcpCsvImportStore;
